import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Footer from './Footer/Footer';
import './mobile.css';
import Header from './Header/HeaderApps/HeaderAllApps';
import AllApps from './Landing/Landing';
import {HmsTabs } from "./Hospitality/Tabs/HmsTabs"
import MobileEmail from './Email/Mobileemail';
import MobileCalendar from './mobileCalender/Mobilecalendar';
/***
 * * Mobile Component 
 * * This component serves as the mobile version of the application.
 * * It includes a header, a landing section, and a footer.
 * * An app that has its own full-screen mobile design (Email for now) takes over the
 * * whole screen, with its own header and bottom bar, so the shared header and footer are hidden for it.
 * * @param {Object} props - The properties passed to the component.
 * * @returns {JSX.Element} The rendered mobile component.
 */



// Header variants for each section

import HeaderHms from './Header/HeaderHms/Header';
import HeaderApps from './Header/HeaderApps/HeaderAllApps';

// Loaded on demand: it is only evaluated when Hospitality is opened, after every other module has finished starting.
// This also removes any chance of a circular-import start-up error ("Cannot access __WEBPACK_DEFAULT_EXPORT__ before initialization").
const Hospitality = lazy(() => import("./Hospitality/Hospitality"));
const AccountingApp = lazy(() => import("./AccountingPage/Accounting"));
const SpurtApp = lazy(() => import("./MobileSpurt/Spurt"));
const CrmApp = lazy(() => import("./MobileCrm/Crm"));
const FoApp = lazy(() => import("./FnO/Fo"));
const RetailApp = lazy(() => import("./MobileRet/Ret"));
const TaskApp = lazy(() => import("./MobileTaskmanager/Tm"));
const CallApp = lazy(() => import("./MobileCallTraccker/Ct"));

const Mobile: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Starts on the All Apps screen. Change 'apps' to 'Hms' to start on Hospitality instead.
  const [activeTab, setActiveTab] = useState('apps');

  // The Mail tile on All Apps asks Mobile to open Email without changing the route.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const key = (e as CustomEvent<string>).detail;
      if (key) setActiveTab(key);
    };
    window.addEventListener('cilpron:open-mobile-app', onOpen);
    return () => window.removeEventListener('cilpron:open-mobile-app', onOpen);
  }, []);

  // Email opens only when chosen: from the Mail tile on the Apps screen (path /mail) or the 'email' tab.
  const emailOpen = activeTab === 'email' || /\/mail\/?$/i.test(location.pathname);

  const leaveEmail = () => {
    if (/\/mail\/?$/i.test(location.pathname)) navigate('/');
    setActiveTab('apps');
  };

  // Calendar is a full app of its own too (opened from the Calendar tile or from Email's bottom bar).
  const calendarOpen = activeTab === 'calendar' || /\/calendar\/?$/i.test(location.pathname);

  // Hospitality (HMS) is a full app too: own header, own bottom bar, and its All apps button comes back here.
  const hmsOpen = /^(hms|hospitality)$/i.test(activeTab) || /\/hms\/?$/i.test(location.pathname);

  const leaveHms = () => {
    if (/\/hms\/?$/i.test(location.pathname)) navigate('/');
    setActiveTab('apps');
  };

  // Accounting is a full app too: own header, own bottom bar, and its All apps button comes back here.
  const accountingOpen = /^accounting$/i.test(activeTab) || /\/accounting\/?$/i.test(location.pathname);

  // Spurt (messaging) is a full app too: own header, own bottom bar, and its All apps button comes back here.
  const spurtOpen = /^spurt$/i.test(activeTab) || /\/chennels\/?$/i.test(location.pathname);

  const leaveSpurt = () => {
    if (/\/chennels\/?$/i.test(location.pathname)) navigate('/');
    setActiveTab('apps');
  };

  // CRM is a full app too: own header, own bottom bar, and its All apps button comes back here.
  const crmOpen = /^crm$/i.test(activeTab) || /\/channels\/?$/i.test(location.pathname);

  const leaveCrm = () => {
    if (/\/channels\/?$/i.test(location.pathname)) navigate('/');
    setActiveTab('apps');
  };

  // Finance and Operations is a full app too: own header, own bottom bar, and its All apps button comes back here.
  const foOpen = /^(fo|finance)$/i.test(activeTab) || /\/finance\/?$/i.test(location.pathname);

  const leaveFo = () => {
    if (/\/finance\/?$/i.test(location.pathname)) navigate('/');
    setActiveTab('apps');
  };

  // Retail is a full app too: own header, own bottom bar, and its All apps button comes back here.
  const retailOpen = /^(retail|retails)$/i.test(activeTab) || /\/retails?\/?$/i.test(location.pathname);

  const leaveRetail = () => {
    if (/\/retails?\/?$/i.test(location.pathname)) navigate('/');
    setActiveTab('apps');
  };

  // Task Manager is a full app too: own header, own bottom bar, and its Close button comes back here.
  const taskOpen = /^(task|tasks)$/i.test(activeTab) || /\/tasks?\/?$/i.test(location.pathname);

  const leaveTask = () => {
    if (/\/tasks?\/?$/i.test(location.pathname)) navigate('/');
    setActiveTab('apps');
  };

  // Call tracker (front desk) is a full app too: own header, own bottom bar, and its Close button comes back here.
  const callOpen = /^(calltracker|call-tracker)$/i.test(activeTab) || /\/call-?tracker\/?$/i.test(location.pathname);

  const leaveCall = () => {
    if (/\/call-?tracker\/?$/i.test(location.pathname)) navigate('/');
    setActiveTab('apps');
  };

  const leaveAccounting = () => {
    if (/\/accounting\/?$/i.test(location.pathname)) navigate('/');
    setActiveTab('apps');
  };

  const leaveCalendar = () => {
    if (/\/calendar\/?$/i.test(location.pathname)) navigate('/');
    setActiveTab('apps');
  };

  const renderHeader = () => {
    switch (activeTab) {
      case 'calendar':
        return <HeaderHms/>;
      case 'create':
        return <HeaderHms  />;
      case 'apps':
      case 'accounting':
        return <HeaderApps/>;
      case 'Hms':
      default:
        return <HeaderHms />;
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'calendar':
        return <div>Apps 3</div>;
      case 'create':
        return <div>Apps 2</div>;
      case 'apps':
        return <AllApps />;
      case 'Hms':
      case 'HMS':
      default:
        return <Suspense fallback={null}><Hospitality /></Suspense>;
    }
  };

  // Email is a full app of its own: own header, own bottom bar, and its back button returns to All Apps.
  if (emailOpen) {
    return <MobileEmail onExit={leaveEmail} />;
  }

  if (hmsOpen) {
    return <Suspense fallback={null}><Hospitality onExit={leaveHms} /></Suspense>;
  }

  if (accountingOpen) {
    return <Suspense fallback={null}><AccountingApp onExit={leaveAccounting} /></Suspense>;
  }

  if (crmOpen) {
    return <Suspense fallback={null}><CrmApp onExit={leaveCrm} /></Suspense>;
  }

  if (foOpen) {
    return <Suspense fallback={null}><FoApp onExit={leaveFo} /></Suspense>;
  }

  if (retailOpen) {
    return <Suspense fallback={null}><RetailApp onExit={leaveRetail} /></Suspense>;
  }

  if (taskOpen) {
    return <Suspense fallback={null}><TaskApp onExit={leaveTask} /></Suspense>;
  }

  if (callOpen) {
    return <Suspense fallback={null}><CallApp onExit={leaveCall} /></Suspense>;
  }

  if (spurtOpen) {
    return <Suspense fallback={null}><SpurtApp onExit={leaveSpurt} /></Suspense>;
  }

  if (calendarOpen) {
    return <MobileCalendar onExit={leaveCalendar} />;
  }

  return (
    <div className="BY9zN">
      {renderHeader()}
      {renderContent()}
      <Footer tabs={HmsTabs} activeTab={activeTab} setActiveTab={setActiveTab} />
    </div>
  );
};

export default Mobile;